import type { ButtonProps } from "@bonusx-global/bx-design-system";
import { Button } from "@bonusx-global/bx-design-system";
import { ArrowRightCircleSolid } from "@bonusx-global/bx-icons";
import Box from "@mui/material/Box";
import { useCallback } from "react";
import type { IProductWithCmsDataAndQualification } from "../../../../features/products/type/product-with-cms-data-and-qualification/product-with-cms-data-and-qualification.entity.interface";
import useIsMobile from "../../../../hooks/device/useIsMobile";
import type { BONUSX_SUBSCRIPTION_TIERS } from "../../../../legacy/services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { GENERAL_SUBSCRIPTION_TO_BONUSX_SUBSCRIPTION_TIER_MAP } from "../../../../legacy/services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { useClientConstants } from "../../../hooks/use-client-constants/use-client-constants";
import { useNavigateToPricingPage } from "../../../hooks/use-navigate-to-pricing-page/use-navigate-to-pricing-page";
import { SUBSCRIPTION_PLAN_UPGRADE_BUTTON_ID } from "../constants/button-ids";

interface SubscriptionPlanUpgradeAndRequestButtonProps extends ButtonProps {
  labelKey: string;
  labelFallback: string;
  product?: IProductWithCmsDataAndQualification;
  requestableFromTier?: BONUSX_SUBSCRIPTION_TIERS;
  productId?: string;
  endIcon?: React.ReactNode | null;
  replacePricingPageNavigation?: boolean;
}

function SubscriptionPlanUpgradeButton({
  size,
  labelKey,
  labelFallback,
  product,
  onClick,
  requestableFromTier,
  productId,
  replacePricingPageNavigation,
  endIcon = <ArrowRightCircleSolid />,
  ...rest
}: Readonly<SubscriptionPlanUpgradeAndRequestButtonProps>): JSX.Element {
  const isMobile = useIsMobile();
  const { getValueOrDefault } = useClientConstants();

  const { navigateToPricingPage } = useNavigateToPricingPage();

  const navigateToPricingPageCallback = useCallback(
    function memoizeNavigateToPricingPage() {
      if (requestableFromTier) {
        navigateToPricingPage({ requestableFromTier, productId, replace: replacePricingPageNavigation });
        return;
      }

      const firstTier = product?.orderedTiersThatCanRequestProduct?.[0];
      const requiredTier = firstTier ? GENERAL_SUBSCRIPTION_TO_BONUSX_SUBSCRIPTION_TIER_MAP[firstTier] : undefined;
      navigateToPricingPage({ requestableFromTier: requiredTier, productId, replace: replacePricingPageNavigation });
    },
    [
      requestableFromTier,
      product?.orderedTiersThatCanRequestProduct,
      navigateToPricingPage,
      productId,
      replacePricingPageNavigation,
    ]
  );

  const onClickUpgradeCallback = useCallback(
    function onClickUpgrade(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
      navigateToPricingPageCallback();

      if (onClick) {
        onClick(event);
      }
    },
    [navigateToPricingPageCallback, onClick]
  );

  return (
    <Box position={"relative"}>
      <Button
        id={SUBSCRIPTION_PLAN_UPGRADE_BUTTON_ID}
        variant="contained"
        size={size}
        color="primary"
        onClick={onClickUpgradeCallback}
        endIcon={<ArrowRightCircleSolid />}
        fullWidth={isMobile}
        {...rest}
      >
        {getValueOrDefault(labelKey, labelFallback)}
      </Button>
    </Box>
  );
}

export default SubscriptionPlanUpgradeButton;
